import { api, type StandardChanges, type StandardChangeEmbodiment, type CsStanSpec } from '../api';
import { callout, close, field, input, opt, openDialog, select, todayISO, val } from '../dialog';
import { esc, fmtDate, html, pill, toast } from '../ui';

/**
 * Modifications embodied under CS-STAN.
 *
 * CS-STAN is the EASA list of changes and repairs that may be embodied with no
 * design approval at all — no STC, no minor change approval, no design
 * organisation. This records what was done and, more usefully, what the
 * specification still expects: an AFM supplement, an amendment to the
 * Instructions for Continuing Airworthiness, a re-weighing.
 *
 * None of that blocks recording. The change is embodied whether or not the
 * paperwork has caught up, and refusing the record would only mean the
 * modification goes unrecorded too — but an AFM supplement that was never
 * issued is a finding at the next review, so it is said here rather than there.
 */

const KIND_LABEL: Record<string, string> = {
  STANDARD_CHANGE: 'Standard change',
  STANDARD_REPAIR: 'Standard repair',
};

function row(e: StandardChangeEmbodiment): string {
  const gone = Boolean(e.removedAt);
  return html`
    <div class="row" style="border-left-color:var(--${gone ? 'line-2' : e.outstanding.length ? 'caution' : 'ok'})">
      <div class="bd">
        <div class="t">
          <span style="font-family:var(--mono)">${esc(e.reference)}</span>
          <span class="sub2">${esc(KIND_LABEL[e.kind] ?? e.kind)}</span>
          ${e.specId ? '' : '<span class="sub2">not in the catalogue</span>'}
        </div>
        <div class="m">${esc(e.title)}</div>
        <div class="m tiny dim">
          embodied ${esc(fmtDate(e.embodiedAt))}
          ${e.crs ? ` · released on ${esc(e.crs.reference)}` : ''}
          ${e.weighing ? ` · weighed ${esc(e.weighing.reference)}` : ''}
          ${e.afmSupplementReference ? ` · AFMS ${esc(e.afmSupplementReference)}` : ''}
          ${gone ? ` · removed ${esc(fmtDate(e.removedAt!))} — ${esc(e.removedReason ?? '')}` : ''}
        </div>
        ${e.outstanding.length && !gone
          ? html`<ul class="tiny" style="margin:5px 0 0 16px;color:var(--caution)">
              ${e.outstanding.map((o) => `<li>${esc(o)}</li>`).join('')}
            </ul>`
          : ''}
      </div>
      <div style="display:flex;gap:6px;align-items:center;white-space:nowrap;flex:none">
        ${gone
          ? pill('NA', 'Removed')
          : e.outstanding.length
            ? pill('DUE_SOON', `${e.outstanding.length} outstanding`)
            : pill('OK', 'Complete')}
        ${gone ? '' : html`<button class="btn sm" data-remove-sc="${esc(e.id)}">Remove</button>`}
      </div>
    </div>
  `;
}

export function standardChangesTab(d: StandardChanges): string {
  const live = d.embodiments.filter((e) => !e.removedAt);
  const gone = d.embodiments.filter((e) => e.removedAt);

  if (!d.eligibility.eligible) {
    return html`
      <div class="stack">
        ${callout('info', 'i', html`
          <b>CS-STAN does not reach ${esc(d.registration)}.</b>
          <ul style="margin:6px 0 0 18px">
            ${d.eligibility.reasons.map((r) => `<li>${esc(r)}</li>`).join('')}
          </ul>
          A change on this aircraft needs an approved design change — an STC or a minor change approval
          — rather than a standard change.
        `)}
      </div>
    `;
  }

  return html`
    <div class="stack">
      ${callout('info', 'i', html`
        A standard change or repair may be embodied with no design approval, provided the conditions in
        CS-STAN are met. Recording it here does not decide that they were — whether a given change suits
        this aircraft is a judgement, and the person embodying it signs for that.
        ${d.catalogueSize === 0
          ? html`<br><br><b>The CS-STAN catalogue has not been imported on this installation</b>, so a
              reference is recorded with a title typed by hand and none of the conditions are checked.`
          : ''}
      `)}

      <div class="card">
        <div class="card-h">
          <div>
            <h2>Embodied under CS-STAN</h2>
            <p class="tiny dim">
              ${live.length} in place${gone.length ? ` · ${gone.length} removed` : ''}${
                d.catalogueSize ? ` · catalogue: ${d.catalogueSize} entries` : ''
              }
            </p>
          </div>
          <button class="btn pri sm" data-add-sc>Record one</button>
        </div>
        <div class="rows">
          ${live.length
            ? live.map(row).join('')
            : '<div class="row"><div class="bd"><div class="t">Nothing recorded</div><div class="m">Standard changes embodied on this aircraft appear here.</div></div></div>'}
          ${gone.map(row).join('')}
        </div>
        <div class="card-f">
          A removed modification keeps its record — the aircraft flew with it embodied, and the next
          review reads that history.
        </div>
      </div>
    </div>
  `;
}

/** Records one, offering the catalogue where it has been imported. */
export function recordStandardChange(
  aircraftId: string,
  catalogue: CsStanSpec[],
  reload: () => void,
): void {
  let chosen: CsStanSpec | undefined;

  const conditions = (spec: CsStanSpec | undefined) => {
    if (!spec) return '';
    const items: string[] = [];
    if (spec.pilotOwnerMayRelease === false) {
      items.push('Not suitable for release to service by the pilot-owner — certifying staff must sign it.');
    }
    if (spec.needsAfmSupplement) items.push('Requires an AFM supplement.');
    if (spec.needsIcaAmendment) items.push('Requires the Instructions for Continuing Airworthiness to be amended.');
    if (spec.affectsWeightAndBalance) items.push('Affects weight and balance — the aircraft needs re-weighing.');
    if (!items.length) return '';
    return callout('info', 'i', html`
      <b>${esc(spec.id)} — what CS-STAN attaches to it</b>
      <ul style="margin:6px 0 0 18px">${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>
    `);
  };

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record a standard change',
    sub: 'CS-STAN — embodied with no design approval, provided its conditions are met',
    primary: 'Record it',
    body: () => html`
      ${catalogue.length
        ? field('Standard change', select('reference', [
            opt('', 'Choose from CS-STAN…'),
            ...catalogue.map((c) => opt(c.id, `${c.id} — ${c.title.slice(0, 70)}`, c.id === chosen?.id)),
          ].join(''), 'data-spec'))
        : html`
            ${field('Reference', input('reference', '', 'placeholder="CS-SC005b" style="font-family:var(--mono)" maxlength="20"'))}
            ${field('What it is', input('title', '', 'placeholder="Installation of an ADS-B OUT system" maxlength="400"'))}
          `}
      ${conditions(chosen)}
      <div class="frow">
        ${field('Embodied on', input('embodiedAt', todayISO(), 'type="date"'))}
        ${field('AFM supplement', input('afmSupplementReference', '', 'placeholder="if one was issued" maxlength="120"'))}
      </div>
      ${field('What was done', input('description', '', 'placeholder="Optional — the detail a later reader needs" maxlength="4000"'))}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-spec]')?.addEventListener('change', (e) => {
        chosen = catalogue.find((c) => c.id === (e.target as HTMLSelectElement).value);
        // Re-rendered so the conditions appear beside the choice rather than
        // after the fact.
        refresh();
      });
    },
    collect: (root) => {
      const reference = val(root, 'reference');
      if (!reference) return { error: 'Choose the standard change, or type its CS-STAN reference.' };
      const body: Record<string, unknown> = {
        reference,
        embodiedAt: val(root, 'embodiedAt'),
        description: val(root, 'description') || undefined,
        afmSupplementReference: val(root, 'afmSupplementReference') || undefined,
      };
      const typedTitle = val(root, 'title');
      if (typedTitle) body.title = typedTitle;
      return body;
    },
    submit: (body) => api.standardChanges.record(aircraftId, body),
    done: (r) => `${r.reference} recorded.`,
    onDone: (r: { outstanding: string[]; pilotOwnerMayRelease: boolean | null }) => {
      // The outstanding list is the point of holding the catalogue, so it is
      // said now rather than left to be noticed on the list behind.
      if (r.outstanding?.length) toast(r.outstanding[0], 'error');
      close();
      reload();
    },
  });
}

/** Wired after the tab renders. */
export function bindStandardChanges(
  root: HTMLElement,
  aircraftId: string,
  catalogue: CsStanSpec[],
  reload: () => void,
): void {
  root.querySelector<HTMLElement>('[data-add-sc]')?.addEventListener('click', () =>
    recordStandardChange(aircraftId, catalogue, reload),
  );

  root.querySelectorAll<HTMLElement>('[data-remove-sc]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const id = btn.dataset.removeSc!;
      openDialog<Record<string, unknown>>({
        title: 'Take this modification off',
        sub: 'The record is kept — the aircraft flew with it embodied',
        primary: 'Record the removal',
        body: () => html`
          ${field('Why', input('reason', '', 'placeholder="Removed at owner request · recorded in error" maxlength="400"'))}
        `,
        collect: (r) => {
          const reason = val(r, 'reason');
          if (!reason.trim()) return { error: 'Say why — the record is what the next review reads.' };
          return { reason };
        },
        submit: (body) => api.standardChanges.remove(aircraftId, id, body),
        done: (r) => `${r.reference} taken off.`,
        onDone: () => reload(),
      });
    }),
  );
}
